import React from "react";
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    Stack,
    Switch,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {useTranslation} from "react-i18next";

import {SHIFT_ADMIN_ALL_FILTER_VALUE, SHIFT_ADMIN_VIEWS} from "../../constants/ShiftAdminConstants";
import {grey} from "@mui/material/colors";

const styles = {
    toolbar: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 2,
        paddingTop: "0.5rem",
        paddingBottom: "0.5rem",
    },
    toolbarGroup: {
        display: "flex",
    },
    dateNavigator: {
        gap: "0.3rem",
        height: "2rem"
    },
    datePicker: {
        maxWidth: "12rem",
        '& .MuiInputBase-root': {
            height: '2rem'
        }
    },
    navButton: {
        borderRadius: "1rem",
        borderColor: grey[400],
        minWidth: "2.2rem",
        height: "2rem",
    },
    navIcon: {
        color: "black",
        fontSize: "0.95rem",
    },
    servicesFilter: {
        minWidth: "8.5rem",
        margin: '0 0.5rem 0 1rem',
        '& .MuiInputBase-root': {
            height: '2rem',
        },
    },
    serviceMenuItem: {
        minHeight: "2rem",
        paddingX: "0.8rem",
        backgroundColor: "#ffffff",
        '&:hover': {
            backgroundColor: "#ffffff",
        },
        '&.Mui-selected': {
            backgroundColor: "#ffffff",
        },
        '&.Mui-selected:hover': {
            backgroundColor: "#ffffff",
        },
    },
    performerFilter: {
        minWidth: "13rem",
        margin: '0 0.5rem 0 1rem',
        '& .MuiInputBase-root': {
            height: '2rem',
        },
    },
    viewToggleGroup: {
        '& .MuiToggleButton-root': {
            minWidth: '4.5rem',
            height: '2rem',
            paddingInline: '1rem',
        },
        '& .MuiToggleButton-root.Mui-selected': {
            backgroundColor: 'primary.main',
            color: '#ffffff'
        },
        '& .MuiToggleButton-root.Mui-selected:hover': {
            backgroundColor: 'primary.main',
            color: '#ffffff'
        }
    },
};

export default function ShiftAdminToolbar({
    activeView,
    onViewChange,
    dateNavigation,
    serviceFilter,
    performerFilter
}) {
    const {t} = useTranslation();
    const {currentDate, onDateChange, onGoPrevious, onGoNext, earliestNavigableDate, isPreviousDateNavigationDisabled} = dateNavigation;
    const {selectedServiceFilters, onServiceFilterChange, serviceFilterOptions} = serviceFilter;
    const {selectedPerformerFilter, onPerformerFilterChange, performersForDropdown} = performerFilter;

    return (
        <Box sx={styles.toolbar}>
            <Stack direction="row" spacing={4} sx={styles.toolbarGroup}>
                <ToggleButtonGroup
                    exclusive
                    value={activeView}
                    onChange={onViewChange}
                    size="small"
                    aria-label="view toggle"
                    sx={styles.viewToggleGroup}>
                    <ToggleButton value={SHIFT_ADMIN_VIEWS.MONTH}>{t("shiftAdmin.toolbar.monthView")}</ToggleButton>
                    <ToggleButton value={SHIFT_ADMIN_VIEWS.DAY}>{t("shiftAdmin.toolbar.dayView")}</ToggleButton>
                </ToggleButtonGroup>

                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={styles.dateNavigator}>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={onGoPrevious}
                        disabled={isPreviousDateNavigationDisabled}
                        aria-label="previous date"
                        sx={styles.navButton}>
                        <ArrowBackIosNewIcon sx={styles.navIcon}/>
                    </Button>

                    <DatePicker
                        value={currentDate}
                        onChange={onDateChange}
                        views={activeView === SHIFT_ADMIN_VIEWS.MONTH ? ["month", "year"] : ["year", "month", "day"]}
                        minDate={earliestNavigableDate}
                        slotProps={{
                            textField: {
                                size: "small",
                            }
                        }}
                        sx={styles.datePicker}/>

                    <Button
                        size="small"
                        variant="outlined"
                        onClick={onGoNext}
                        aria-label="next date"
                        sx={styles.navButton}>
                        <ArrowForwardIosIcon sx={styles.navIcon}/>
                    </Button>
                </Stack>

                <FormControl size="small" sx={styles.servicesFilter}>
                    <InputLabel id="service-filter-label" shrink>
                        {t("shiftAdmin.toolbar.servicesFilterLabel")}
                    </InputLabel>

                    <Select
                        multiple
                        displayEmpty
                        labelId="service-filter-label"
                        value={selectedServiceFilters}
                        label={t("shiftAdmin.toolbar.servicesFilterLabel")}
                        onChange={onServiceFilterChange}
                        renderValue={(selectedServices) => (`${selectedServices.length} Selected`)}
                        MenuProps={{MenuListProps: {sx: {paddingBottom: 0}}}}>
                        {serviceFilterOptions.map((serviceOption) => (
                            <MenuItem key={serviceOption} value={serviceOption} sx={styles.serviceMenuItem}>
                                <ListItemText primary={serviceOption}/>
                                <Switch size="small" checked={selectedServiceFilters.includes(serviceOption)} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={styles.performerFilter}>
                    <InputLabel id="performer-filter-label">{t("shiftAdmin.toolbar.allPerformersOption")}</InputLabel>

                    <Select
                        labelId="performer-filter-label"
                        value={selectedPerformerFilter}
                        label={t("shiftAdmin.toolbar.allPerformersOption")}
                        onChange={onPerformerFilterChange}
                        MenuProps={{MenuListProps: {sx: {paddingBottom: 0}}}}>
                        <MenuItem value={SHIFT_ADMIN_ALL_FILTER_VALUE}>{t("shiftAdmin.toolbar.allPerformersOption")}</MenuItem>

                        {performersForDropdown.map((performerItem) => (
                            <MenuItem key={performerItem.id} value={performerItem.id}>
                                {performerItem.fullName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>
            {
                activeView === SHIFT_ADMIN_VIEWS.MONTH
                &&
                <Button variant="outlined" size="small" endIcon={<FileDownloadOutlinedIcon/>}>
                    {t("shiftAdmin.toolbar.exportReport")}
                </Button>
            }
        </Box>
    );
}
